/**
 * Centralized affiliate URLs + GA4 click-attribution layer.
 *
 * Why this file exists:
 *  - Single source of truth for outbound revenue links (kills drift risk).
 *  - Funnels every affiliate click through one GA4 event (`affiliate_click`)
 *    so revenue attribution is queryable in GA4 Reports → Engagement → Events.
 *
 * GA4 event shape:
 *    event_name   = "affiliate_click"
 *    parameters   = { partner, product, source_page, destination }
 *
 * In GA4 → Admin → Custom definitions, register the four parameters
 * (partner, product, source_page, destination) as custom dimensions to
 * unlock per-partner / per-page / per-product breakdowns in Explorations.
 */

const REF = "MARKBECKER";

export const AFFILIATE_URLS = {
  // Paramount Peptides — main catalog landing
  paramount: `https://paramountpeptides.com/?ref=${REF}`,
  // Paramount Peptides — topical peptides category
  topicals: `https://paramountpeptides.com/product-category/topical-peptides/?ref=${REF}`,
  // Paramount Peptides — Partner / Affiliate Program registration
  partnerProgram: `https://partners.paramountpeptides.com/create-account?ref=${REF}`,
  // AlphaBioMed Labs — verified-provider account program
  providerAccess: `https://alphabiomedlabs.com/pages/register?ref=${REF}`,
  // Educational referral partners (no ref code — informational only)
  alphasync: "https://www.alphasyncrx.com/",
  mydose: "https://mydose.ai/",
  // Skincare brand partner — features a GHK-Cu facemask we recommend
  alohi: "https://www.alohiskinandwellness.com/",
};

/**
 * Fire a GA4 `affiliate_click` event. Safe to call from any component —
 * silently no-ops if gtag isn't loaded (e.g., adblocker, dev environment).
 */
export function trackAffiliateClick({ partner, product = null, destination, sourcePage = null }) {
  try {
    if (typeof window === "undefined") return;
    const gtag = window.gtag;
    if (typeof gtag !== "function") return;

    gtag("event", "affiliate_click", {
      partner,
      product: product || "(none)",
      source_page: sourcePage || window.location.pathname || "(unknown)",
      destination,
      // GA4 also auto-captures page_location, page_title, language, etc.
    });
  } catch (e) {
    // Tracking must never break the user's click.
    console.error("[affiliate] tracking failed:", e);
  }
}

/**
 * Returns an onClick handler that fires the GA4 event before the browser
 * follows the link (or before window.open is called).
 *
 * Usage:
 *   <a href={AFFILIATE_URLS.paramount}
 *      onClick={onAffiliateClick({ partner: "paramount", product: "ghk-cu" })}
 *      target="_blank" rel="noopener noreferrer nofollow sponsored">
 */
export function onAffiliateClick(meta) {
  return () => {
    trackAffiliateClick({
      ...meta,
      destination: meta.destination || meta.url,
    });
  };
}
